import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Printer, Edit, Check, X, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const IRItemsTable = ({
  indentID,
  calculateTotalAmount,
  isDraft,
  isEditable = true,
  onSubmit,
  onPrint,
  onCloseIndent,
  onIndentStatusChange,
  forceClosureStatus,
  userId,
  isClosed = false,
  indentStatus = { l1a: 0, l1: 0, l2: 0, po: 0, ph: 0, ir: 0, sh: 1 },
}) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [editModes, setEditModes] = useState({});
  const [tempQuantities, setTempQuantities] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

    // Format number to 3 decimal places
    const formatDecimal = (value) => {
      return Number(parseFloat(value).toFixed(3));
    };

  // Fetch items based on the current page context
  useEffect(() => {
    const fetchIndentItems = async () => {
      if (!indentID) return;

      try {
        const response = await axios.get(
          `${API_BASE_URL}/viewTable/indentitems`,
          {
            params: { indentId: indentID },
          }
        );

        const fetchedItems = response.data
          .filter((item) => item.IndentID === indentID)
          .map((item) => ({
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: formatDecimal(item.Quantity),
            quantityReceived: formatDecimal(item.QuantityReceived || 0),
            difference: formatDecimal(item.Difference || 0),
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            commitedDate: item.CommitedDate,
            accountingUnitID: item.AccountingUnitID,
            unitname: item.UnitName,
          }));

        setItems(fetchedItems);
      } catch (error) {
        console.error("Error fetching indent items:", error);
        setError("Failed to load indent items");
      }
    };

    fetchIndentItems();
  }, [indentID]);

  const handleQuantityReceived = async (index, quantityReceived) => {
    const currentItem = items[index];
    const safeQuantityReceived = quantityReceived === "" ? 0 : formatDecimal(quantityReceived);
    const difference = formatDecimal(currentItem.quantity - safeQuantityReceived);

    try {
      await axios.put(
        `${API_BASE_URL}/irpage/indentitems/${currentItem.indentItemID}`,
        {
          QuantityReceived: safeQuantityReceived,
          Difference: difference,
        }
      );

      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        quantityReceived: safeQuantityReceived,
        difference,
      };

      setItems(updatedItems);
      setEditModes((prev) => ({ ...prev, [currentItem.indentItemID]: false }));
    } catch (error) {
      console.error("Detailed Error in Quantity Update:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update received quantity"
      );
    }
  };

  const toggleEditMode = (itemId, currentQuantity) => {
    setEditModes((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));

    // Set temporary quantity when entering edit mode
    if (!editModes[itemId]) {
      setTempQuantities((prev) => ({
        ...prev,
        [itemId]: currentQuantity,
      }));
    }
  };

  const handleQuantityChange = (itemId, value) => {
    setTempQuantities((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleCloseIndent = async () => {
    try {
      await axios.post(`${API_BASE_URL}/irpage/indents/close`, {
        indentId: indentID,
        userId: userId,
        forceClose: false,
      });

      // Option 1: If using React Router
      // Assuming you have access to the navigation function
      navigate("/IRPage");

      // Option 2: Full page reload if not using React Router
      window.location.href = "/IRPage";

      // Option 3: If you want to call a parent component method to refresh
      onCloseIndent && onCloseIndent();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to close indent";
      setError(errorMessage);
      console.error("Indent Closure Error:", error.response?.data);
    }
  };

  const handleSendToStoreHead = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/irpage/indents/sendToStoreHead`,
        {
          IndentID: indentID,
          UserID: userId,
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        // Update local state immediately
        const newStatus = {
          ...indentStatus,
          sh: 0,
        };
        onIndentStatusChange && onIndentStatusChange(newStatus);

        // Optionally refresh the page
        navigate("/IRPage", {
          state: { message: "Successfully sent to Store Head for approval" },
        });
      } else {
        throw new Error(response.data.error || "Unexpected server response");
      }
    } catch (error) {
      let errorMessage;
      if (error.response) {
        // Server returned an error response
        errorMessage =
          error.response.data.error || "Failed to send to store head";
      } else if (error.request) {
        // No response received
        errorMessage = "Network error: Please check your connection";
      } else {
        // Something else went wrong
        errorMessage = error.message || "An unexpected error occurred";
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if actions should be disabled
  const shouldDisableActions =
    isClosed || forceClosureStatus || indentStatus.sh === 0;

  // Check if all differences are zero
  const allDifferencesZero = items.every((item) => item.difference === 0);

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
      {/* Status messages */}
      {(isClosed || forceClosureStatus || indentStatus.sh === 0) && (
        <div
          className={`
            ${
              forceClosureStatus
                ? "bg-yellow-100 border-yellow-400 text-yellow-700"
                : indentStatus.sh === 0
                ? "bg-blue-100 border-blue-400 text-blue-700"
                : "bg-green-100 border-green-400 text-green-700"
            }
            px-4 py-3 rounded mb-4
          `}
        >
          {forceClosureStatus
            ? "This Indent was Force Closed"
            : indentStatus.sh === 0
            ? "Sent for approval to Store Head"
            : "This Indent has been Closed"}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {!shouldDisableActions && (
        <div className="mb-4">
          {allDifferencesZero ? (
            <button
              onClick={handleCloseIndent}
              disabled={isSubmitting}
              className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Close Indent
            </button>
          ) : (
            <button
              onClick={handleSendToStoreHead}
              disabled={isSubmitting}
              className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Send className="mr-2" size={16} />
              {isSubmitting ? "Sending..." : "Send to Store Head"}
            </button>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-center">Quantity Received</th>
              <th className="px-4 py-3 text-center">Difference</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Committed Date</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className="border-b hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                <td className="px-4 py-3 font-medium">{item.itemName}</td>
                <td className="px-4 py-3 font-medium">{item.unitname}</td>
                <td className="px-4 py-3 text-center">{item.quantity.toFixed(3)}</td>

                {/* Quantity Received Column */}
                <td className="px-4 py-3 text-center flex items-center justify-center">
                  {shouldDisableActions ? (
                    item.quantityReceived.toFixed(3)
                  ) : (
                    <>
                      {editModes[item.indentItemID] ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            step="0.001"
                            className="w-20 border rounded px-2 py-1 text-center"
                            value={tempQuantities[item.indentItemID] || ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? 0
                                  : parseFloat(e.target.value);
                              handleQuantityChange(item.indentItemID, value);
                            }}
                            min="0"
                          />
                          <button
                            className="text-green-500 hover:bg-green-100 rounded-full p-1"
                            onClick={() =>
                              handleQuantityReceived(
                                index,
                                tempQuantities[item.indentItemID]
                              )
                            }
                          >
                            <Check size={16} />
                          </button>
                          <button
                            className="text-red-500 hover:bg-red-100 rounded-full p-1"
                            onClick={() => toggleEditMode(item.indentItemID)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <span>{item.quantityReceived.toFixed(3)}</span>
                          <button
                            className="text-blue-500 hover:bg-blue-100 rounded-full p-1"
                            onClick={() =>
                              toggleEditMode(
                                item.indentItemID,
                                item.quantityReceived
                              )
                            }
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="px-4 py-3 text-center">{item.difference.toFixed(3)}</td>
                <td className="px-4 py-3 text-right">{item.stock}</td>
                <td className="px-4 py-3 text-right">
                  {item.commitedDate
                    ? new Date(item.commitedDate).toLocaleDateString()
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IRItemsTable;
